import React from 'react';
import { Card, CardContent, CardHeader, Typography, Box, Stack, Chip, IconButton } from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Group as GroupIcon,
    Money,
    ContentCopy as DuplicateIcon,
} from '@mui/icons-material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { IRole } from '../../../types/global/AuditionDetailsEditor.types';
import { RichTextViewer } from '../../Common/RichTextViewer';

export interface IAuditionRoleCard {
    role: IRole;
    editMode?: boolean;
    onEdit?: (role: IRole) => void;
    onDelete?: (role: IRole) => void;
    onDuplicate?: (role: IRole) => void;
}

const AuditionRoleCard: React.FC<IAuditionRoleCard> = ({ role, onEdit, onDelete, onDuplicate }) => {
    const { themeStyle } = useThemeContext();
    const { character, description, age_range, gender, expertise, gig_type, displayCurrencyValue } = role;

    // Status badge logic (for now, always 'Open')
    const statusLabel = 'Open';
    const statusColor = {
        bg: theme[themeStyle].green_status_indicator,
        text: theme[themeStyle].green_status_indicator_full,
        border: theme[themeStyle].green_status_indicator_full,
    };

    // Age range display
    const displayAge = age_range ? `${age_range[0]} - ${age_range[1]}` : undefined;

    return (
        <Card
            sx={{
                borderRadius: 2,
                border: `1px solid ${theme[themeStyle].tag_white}`,
                borderLeft: `4px solid ${theme[themeStyle].highlight}`,
                backgroundColor: theme[themeStyle].deep_grey_lighter,
                boxShadow: 'none',
                p: 0,
                overflow: 'visible',
                position: 'relative',
                minHeight: 180,
            }}>
            <CardHeader
                sx={{
                    pb: 1,
                    pt: 2,
                    px: 3,
                    '& .MuiCardHeader-content': { minWidth: 0 },
                }}
                action={
                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton
                            size="small"
                            aria-label="edit"
                            onClick={() => onEdit?.(role)}
                            sx={{ color: theme[themeStyle].tag_white, mr: 0.5 }}>
                            <EditIcon />
                        </IconButton>
                        <IconButton
                            size="small"
                            aria-label="duplicate"
                            onClick={() => onDuplicate?.(role)}
                            sx={{ color: theme[themeStyle].tag_white, mr: 0.5 }}>
                            <DuplicateIcon />
                        </IconButton>
                        <IconButton
                            size="small"
                            aria-label="delete"
                            onClick={() => onDelete?.(role)}
                            sx={{ color: theme[themeStyle].primary_red }}>
                            <DeleteIcon />
                        </IconButton>
                    </Stack>
                }
                title={
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <Chip
                            label={statusLabel}
                            size="small"
                            sx={{
                                backgroundColor: statusColor.bg,
                                color: statusColor.text,
                                border: `1px solid ${statusColor.border}`,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '0.85rem',
                                fontWeight: 500,
                            }}
                        />
                        {displayAge && (
                            <Chip
                                label={displayAge}
                                size="small"
                                sx={{
                                    backgroundColor: theme[themeStyle].tag_white_light,
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                }}
                            />
                        )}
                    </Stack>
                }
            />
            <CardContent sx={{ pt: 0, px: 3, pb: 2 }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: theme[themeStyle].tag_white,
                        fontSize: '1.25rem',
                        mb: 0.5,
                        lineHeight: 1.2,
                    }}>
                    {character}
                </Typography>

                <RichTextViewer
                    content={description}
                    textStyle={{
                        padding: 0,
                        color: theme[themeStyle].darkGray,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: '0.8rem',
                        marginBottom: 2,
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Stack direction="row" spacing={3} alignItems="center">
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme[themeStyle].darkGray,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '1rem',
                                textTransform: 'capitalize',
                                fontWeight: '700',
                            }}>
                            {gender}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme[themeStyle].darkGray,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '1rem',
                            }}>
                            {expertise && typeof expertise === 'object' && expertise && 'value' in expertise
                                ? expertise.value
                                : expertise}
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={3} alignItems="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <GroupIcon sx={{ fontSize: 20, color: theme[themeStyle].darkGray }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme[themeStyle].darkGray,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '1rem',
                                }}>
                                {gig_type && typeof gig_type === 'object' && gig_type && 'value' in gig_type
                                    ? gig_type.value
                                    : expertise}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Money sx={{ fontSize: 20, color: theme[themeStyle].darkGray }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme[themeStyle].darkGray,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '1rem',
                                }}>
                                {displayCurrencyValue}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
            </CardContent>
        </Card>
    );
};

export { AuditionRoleCard };
